/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit cart events from the cart-drawer scope.
 * - openCart:  open the cart drawer IN PLACE so the shopper stays on the page
 *              and can keep adding items. We only navigate to /cart as a true
 *              last resort (no cart drawer exists on the page, e.g. the cart
 *              page itself or when cart type is not 'drawer').
 *
 * Why this file exists / what it fixes:
 * The previous handler used `if (drawer?.open) drawer.open()` and otherwise
 * redirected. `open` is only a callable method once the <theme-drawer> custom
 * element has upgraded; before that (or after a Section Rendering morph swaps
 * the drawer node) `drawer.open` is undefined, so every add-to-cart bounced
 * the shopper to /cart. We now wait for the element to upgrade and open it,
 * and never redirect while a drawer is present.
 */
(function () {
  var CART_DRAWER_SELECTOR = 'theme-drawer#cart-drawer';

  function getCartDrawer() {
    return document.querySelector(CART_DRAWER_SELECTOR);
  }

  function cartUrl() {
    try {
      if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) {
        return Theme.routes.cart_url;
      }
    } catch (e) {
      /* Theme not defined yet */
    }
    return '/cart';
  }

  // Open the given drawer element as robustly as possible.
  function openDrawer(drawer) {
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    // Fallback for the rare case the element still is not upgraded: reflect the
    // open state so the CSS shows the panel, and promote the inner dialog.
    drawer.setAttribute('open', '');
    var dialog = drawer.querySelector('dialog');
    if (dialog && !dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      } catch (e) {
        dialog.setAttribute('open', '');
      }
    }
  }

  function openCartDrawer() {
    var drawer = getCartDrawer();

    // No drawer on this page at all -> nothing to open, fall back to the cart page.
    if (!drawer) {
      window.location.href = cartUrl();
      return;
    }

    if (typeof drawer.open === 'function' || !window.customElements) {
      openDrawer(drawer);
      return;
    }

    // Element is present but not yet upgraded: wait for it, then open. Never
    // redirect just because the definition has not landed yet.
    window.customElements
      .whenDefined('theme-drawer')
      .then(function () {
        openDrawer(getCartDrawer() || drawer);
      })
      .catch(function () {
        openDrawer(drawer);
      });
  }

  function configureAction(name, config) {
    try {
      var actions = window.Shopify && window.Shopify.actions;
      var action = actions && actions[name];
      if (action && typeof action.configure === 'function') {
        action.configure(config);
        return true;
      }
    } catch (e) {
      /* Retry on the next tick */
    }
    return false;
  }

  function apply() {
    var okUpdate = configureAction('updateCart', {
      eventTarget: function () {
        return getCartDrawer() || document;
      },
    });
    var okOpen = configureAction('openCart', {
      async handler() {
        openCartDrawer();
      },
    });
    return okUpdate && okOpen;
  }

  function init() {
    if (apply()) return;
    // Shopify.actions may attach after this module runs; retry briefly (~2s).
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (apply() || tries > 40) clearInterval(timer);
    }, 50);
  }

  if (window.Shopify && window.Shopify.actions) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    window.addEventListener('load', init, { once: true });
  }
})();
